import {atom} from 'jotai';
import {atomWithStorage} from 'jotai/utils';

export type ThemeModePreference = 'light' | 'dark' | 'system';

const prefersDarkQuery = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null;

// Tracks the OS-level preference; kept in sync via subscribeSystemTheme.
export const systemPrefersDarkAtom = atom(prefersDarkQuery?.matches ?? false);

/**
 * Call once near the app root (e.g. in a top-level useEffect) to keep
 * systemPrefersDarkAtom updated when the OS setting changes:
 *
 *   const setSystemPrefersDark = useSetAtom(systemPrefersDarkAtom);
 *   useEffect(() => subscribeSystemTheme(setSystemPrefersDark), []);
 */
export function subscribeSystemTheme(setSystemPrefersDark: (v: boolean) => void) {
   if (!prefersDarkQuery) return () => {};
   const handler = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);
   prefersDarkQuery.addEventListener('change', handler);
   return () => prefersDarkQuery.removeEventListener('change', handler);
}

// Persisted user choice: 'light' | 'dark' | 'system'.
export const themeModePreferenceAtom = atomWithStorage<ThemeModePreference>('themeModePreference', 'system');

// The actual mode to render, resolving 'system' against the OS preference.
export const resolvedThemeModeAtom = atom((get) => {
   const preference = get(themeModePreferenceAtom);
   if (preference === 'system') {
      return get(systemPrefersDarkAtom) ? 'dark' : 'light';
   }
   return preference;
});
